import { useCallback } from 'react';
import toast from 'react-hot-toast';
import { getSplashScreenService } from '../service/ServiceRegistry';
import type { FetchDataFn, TableDataResponse, TableQueryParams } from '../types/table';
import type {
  SplashScreen,
  SplashScreenCreateRequest,
  SplashScreenUpdateRequest,
  SplashScreenQueryParams,
  SplashScreensData,
} from '../types/api/splash-screen';

/**
 * Hook for fetching splash screens (for DataTable)
 */
export function useSplashScreens() {
  const splashScreenService = getSplashScreenService();

  const fetchSplashScreens: FetchDataFn<SplashScreen> = useCallback(
    async (params: TableQueryParams): Promise<TableDataResponse<SplashScreen>> => {
      const apiParams: SplashScreenQueryParams = {
        page: params.page,
        size: params.pageSize,
      };

      if (params.filters?.isActive?.value !== undefined && params.filters.isActive.value !== '') {
        const isActiveValue = params.filters.isActive.value;
        if (typeof isActiveValue === 'string') {
          apiParams.isActive = isActiveValue === 'true';
        } else {
          apiParams.isActive = Boolean(isActiveValue);
        }
      }
      if (params.filters?.targetPlatform?.value) {
        apiParams.targetPlatform = String(params.filters.targetPlatform.value);
      }
      if (params.filters?.language?.value) {
        apiParams.language = String(params.filters.language.value);
      }

      const response: SplashScreensData = await splashScreenService.list(apiParams);

      return {
        data: response.items,
        pagination: {
          page: response.page,
          pageSize: response.size,
          total: response.totalItems,
          totalPages: response.totalPages,
          hasNext: response.hasNext,
          hasPrev: response.hasPrevious,
        },
      };
    },
    [splashScreenService]
  );

  return { fetchSplashScreens };
}

/**
 * Hook for splash screen CRUD actions
 */
export function useSplashScreenActions() {
  const splashScreenService = getSplashScreenService();

  const createWithImage = useCallback(
    async (payload: SplashScreenCreateRequest, imageFile: File): Promise<SplashScreen> => {
      try {
        const splash = await splashScreenService.createWithImage(payload, imageFile);
        toast.success('Splash screen created successfully');
        return splash;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to create splash screen';
        toast.error(message);
        throw error;
      }
    },
    [splashScreenService]
  );

  const createWithJson = useCallback(
    async (payload: SplashScreenCreateRequest): Promise<SplashScreen> => {
      try {
        const splash = await splashScreenService.createWithJson(payload);
        toast.success('Splash screen created successfully');
        return splash;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to create splash screen';
        toast.error(message);
        throw error;
      }
    },
    [splashScreenService]
  );

  const getSplashScreen = useCallback(
    async (splashId: string): Promise<SplashScreen> => {
      try {
        return await splashScreenService.getById(splashId);
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to fetch splash screen';
        toast.error(message);
        throw error;
      }
    },
    [splashScreenService]
  );

  const updateWithImage = useCallback(
    async (
      splashId: string,
      payload: SplashScreenUpdateRequest,
      imageFile?: File
    ): Promise<SplashScreen> => {
      try {
        const splash = await splashScreenService.updateWithImage(splashId, payload, imageFile);
        toast.success('Splash screen updated successfully');
        return splash;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to update splash screen';
        toast.error(message);
        throw error;
      }
    },
    [splashScreenService]
  );

  const updateWithJson = useCallback(
    async (splashId: string, payload: SplashScreenUpdateRequest): Promise<SplashScreen> => {
      try {
        const splash = await splashScreenService.updateWithJson(splashId, payload);
        toast.success('Splash screen updated successfully');
        return splash;
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to update splash screen';
        toast.error(message);
        throw error;
      }
    },
    [splashScreenService]
  );

  const deleteSplashScreen = useCallback(
    async (splashId: string): Promise<void> => {
      try {
        await splashScreenService.delete(splashId);
        toast.success('Splash screen deleted successfully');
      } catch (error: unknown) {
        const message = error instanceof Error ? error.message : 'Failed to delete splash screen';
        toast.error(message);
        throw error;
      }
    },
    [splashScreenService]
  );

  return {
    createWithImage,
    createWithJson,
    getSplashScreen,
    updateWithImage,
    updateWithJson,
    deleteSplashScreen,
  };
}
